import type {DalValue, CreateExtArgs, ExtensionAPI} from '@wix/document-manager-core'
import type {
    CompRef,
    Pointer,
    DependenciesManager,
    NamespacesDataMap,
    RefInfo,
    ComponentFeature,
    DependenciesBuilder,
    SchemaService
} from '@wix/document-services-types'
import _ from 'lodash'
import type {DataModelExtensionAPI} from '../../../../dataModel/dataModel'
import {
    DetailedValue,
    forEachReferredDataItem,
    getDetailedValueByPath,
    referencedMapToNamespace,
    type ExtendedRefInfo,
    type ReferredDataItemsIterateeContext
} from '@wix/import-export-utils'
import {addHash, stripHashIfExists} from '../../../../../utils/refArrayUtils'
import type {RelationshipsAPI} from '../../../../relationships'
import type {SchemaExtensionAPI} from '../../../../..'
import {REFERABLE_BY_EXTERNAL_NODES, type ExternalsHandler} from '../../constants'
import {getExternalsHandler} from '../externals'
import {
    COMP_DATA_QUERY_KEYS_WITH_STYLE,
    DATA_TYPES,
    INNER_ELEMENT_TYPE,
    MASTER_PAGE_ID
} from '../../../../../constants/constants'
import {flatToNestedTransformMap, nestedToFlatTransformMap} from '../../../../builderComponents/builderComponentsHooks'
import type {SchemaAPI} from '../../../../schema/schema'
import {SPECIAL_DATA_NODE_IDS} from './dependencies'
import {ReportableError} from '@wix/document-manager-utils'
import {getProbeIdStagesForPolymorphicReferencedMap, resolveEffectiveReferencedMap} from './effectiveReferencedMap'

interface DataItemAdapterContext {
    schemaService: SchemaService
    extensionAPI: ExtensionAPI
    dataItem: DalValue
    namespace: string
    schemaAPI: SchemaAPI
    componentType?: string
}

interface ItemIdAccessor {
    get(): string | undefined
    set(id: string): void
}

interface DataItemAdapter {
    shouldApply(ctx: DataItemAdapterContext): boolean
    forExport(ctx: DataItemAdapterContext): DalValue
    forImport(ctx: DataItemAdapterContext): DalValue
    resolveItemId?(item: DalValue, namespace: string): ItemIdAccessor | undefined
}

const nestedToFlatItem = (dataItem: any) => {
    return nestedToFlatTransformMap[DATA_TYPES.data](dataItem)
}

const flatToNestedItem = (item: any, type: string) => {
    return flatToNestedTransformMap[DATA_TYPES.data](item, type)
}

const flatToNestedItemTransformer =
    ({schemaAPI}: {schemaAPI: SchemaAPI}) =>
    ({item, namespace: itemNamespace, itemRefInfo, ownerItem}: ReferredDataItemsIterateeContext) => {
        if (
            itemNamespace === DATA_TYPES.data &&
            ownerItem?.type === INNER_ELEMENT_TYPE &&
            itemRefInfo?.path?.[0] === COMP_DATA_QUERY_KEYS_WITH_STYLE.data
        ) {
            const dataType = schemaAPI.getDefinition(ownerItem.referredType)?.builderTypes?.data
            if (dataType) {
                ownerItem[COMP_DATA_QUERY_KEYS_WITH_STYLE.data] = flatToNestedItem(item, dataType)
            }
        }
    }

const nestedToFlatItemTransformer = ({
    item,
    namespace: itemNamespace,
    itemRefInfo,
    ownerItem
}: ReferredDataItemsIterateeContext) => {
    if (
        itemNamespace === DATA_TYPES.data &&
        ownerItem?.type === INNER_ELEMENT_TYPE &&
        itemRefInfo?.path?.[0] === COMP_DATA_QUERY_KEYS_WITH_STYLE.data
    ) {
        ownerItem[COMP_DATA_QUERY_KEYS_WITH_STYLE.data] = nestedToFlatItem(item)
    }
}

const builderDataAdapter: DataItemAdapter = {
    shouldApply: ({namespace, componentType, schemaAPI}) =>
        (namespace === DATA_TYPES.data || namespace === DATA_TYPES.innerElements) &&
        !!componentType &&
        schemaAPI.isSchemaCollectionRegistered(componentType),

    resolveItemId: (item: DalValue, namespace: string): ItemIdAccessor | undefined => {
        if (namespace === DATA_TYPES.data && item._id !== undefined) {
            return {
                get: () => item._id,
                set: (id: string) => {
                    item._id = id
                }
            }
        }
        return undefined
    },

    forExport: ({dataItem, schemaAPI, componentType, namespace, schemaService}) => {
        if (namespace === DATA_TYPES.innerElements) {
            forEachReferredDataItem(schemaService, dataItem, namespace, flatToNestedItemTransformer({schemaAPI}))

            return dataItem
        }

        const dataItemType = schemaAPI.getDataItemTypeBasedOnCompType(componentType!)
        return dataItemType ? flatToNestedItem(dataItem, dataItemType) : dataItem
    },

    forImport: ({dataItem, namespace, schemaService}) => {
        if (namespace === DATA_TYPES.innerElements) {
            forEachReferredDataItem(schemaService, dataItem, namespace, nestedToFlatItemTransformer)
            return dataItem
        }

        const result = nestedToFlatItem(dataItem)

        return result
    }
}

const dataItemAdapters: DataItemAdapter[] = [builderDataAdapter]

const adaptDataItemForExport = (
    schemaService: SchemaService,
    extensionAPI: ExtensionAPI,
    dataItem: DalValue,
    namespace: string,
    schemaAPI: SchemaAPI,
    componentType?: string
): DalValue => {
    if (!dataItem) {
        return dataItem
    }

    const ctx: DataItemAdapterContext = {schemaService, extensionAPI, dataItem, namespace, schemaAPI, componentType}
    const matchingAdapters = dataItemAdapters.filter(a => a.shouldApply(ctx))
    return matchingAdapters.reduce((item, adapter) => adapter.forExport({...ctx, dataItem: item}), dataItem)
}

const adaptDataItemForImport = (
    schemaService: SchemaService,
    extensionAPI: ExtensionAPI,
    dataItem: DalValue,
    namespace: string,
    schemaAPI: SchemaAPI,
    componentType?: string
): DalValue => {
    if (!dataItem) {
        return dataItem
    }

    const ctx: DataItemAdapterContext = {schemaService, extensionAPI, dataItem, namespace, schemaAPI, componentType}
    const matchingAdapters = dataItemAdapters.filter(a => a.shouldApply(ctx))
    return matchingAdapters.reduce((item, adapter) => adapter.forImport({...ctx, dataItem: item}), dataItem)
}

interface DataItemsUpdate {
    pointer: Pointer
    origDataItem?: DalValue
    dataItem?: DalValue
}

type DataItemsUpdates = DataItemsUpdate[]

const sanitizeDataItem = (
    dataItem: DalValue,
    namespace: string,
    isBuilderComponent: boolean,
    isPersistentDataNode: boolean
) => {
    const {id, type} = dataItem
    if (isBuilderComponent || isPersistentDataNode) {
        return
    }
    if (!REFERABLE_BY_EXTERNAL_NODES[namespace]?.[type] && !SPECIAL_DATA_NODE_IDS[namespace]?.has(id)) {
        delete dataItem.id
    }
}

const hasItemInReferencedMap = ({dal, pointers}: CreateExtArgs, probeId: string, referencedMap: string): boolean => {
    const namespace = referencedMapToNamespace[referencedMap] ?? referencedMap
    return Boolean(dal.get(pointers.getPointer(probeId, namespace)))
}

const getEffectiveReferencedMap = (
    extensionContext: CreateExtArgs,
    refId: string,
    namespace: string,
    itemType: string,
    refFieldInfo: ExtendedRefInfo,
    hasReferenceInMap: (candidateMap: string) => boolean
): string => {
    const probeIdStages = getProbeIdStagesForPolymorphicReferencedMap(refId)
    return resolveEffectiveReferencedMap({
        refId,
        namespace,
        itemType,
        refFieldInfo,
        hasReferenceInMap,
        hasItemInMap: (candidateMap, probeId) => hasItemInReferencedMap(extensionContext, probeId, candidateMap),
        probeIdStages
    })
}

const collectNonOwnedReferences = (
    extensionContext: CreateExtArgs,
    item: DalValue,
    namespace: string,
    refFieldsInfo: ExtendedRefInfo[],
    dependenciesBuilder: DependenciesBuilder
) => {
    const {pointers} = extensionContext
    for (const refFieldInfo of refFieldsInfo) {
        const {isRefOwner, path, constantValues} = refFieldInfo
        if (isRefOwner) {
            continue
        }

        const valueByPath: DetailedValue[] = getDetailedValueByPath(item, path)
        const handleExternals: ExternalsHandler = getExternalsHandler(namespace, item.type, path)

        for (const detailedValue of valueByPath) {
            if (!_.isString(detailedValue.value) || constantValues?.has(detailedValue.value)) {
                continue
            }

            const refId: string = stripHashIfExists(detailedValue.value)
            const effectiveReferencedMap = getEffectiveReferencedMap(
                extensionContext,
                refId,
                namespace,
                item.type,
                refFieldInfo,
                candidateMap => dependenciesBuilder.references.hasInternalRef(refId, candidateMap)
            )
            const refPointer: Pointer = pointers.getPointer(refId, effectiveReferencedMap)
            handleExternals.pack(detailedValue, dependenciesBuilder, refPointer, item)
        }
    }
}

const replaceRefArrayPersistentRefs = (
    extensionContext: CreateExtArgs,
    item: DalValue,
    namespace: string,
    dependenciesBuilder: DependenciesBuilder
) => {
    const {extensionAPI, pointers} = extensionContext
    const {dataModel} = extensionAPI as DataModelExtensionAPI

    item.values = (item.values ?? []).map((value: string | Record<string, unknown>) => {
        if (_.isObject(value) && 'id' in value && typeof value.id === 'string') {
            const pointer = pointers.getPointer(value.id, namespace)
            if (dataModel.isPersistentDataNode(pointer)) {
                dependenciesBuilder.references.addNonOwnedRef(value.id, namespace)
                return addHash(value.id)
            }
        }
        return value
    })
}

const replaceVariantRelationPersistentRefs = (
    extensionContext: CreateExtArgs,
    item: DalValue,
    namespace: string,
    dependenciesBuilder: DependenciesBuilder
) => {
    if (item.type !== 'VariantRelation') {
        return
    }

    const {extensionAPI, pointers} = extensionContext
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const value = item.to

    if (_.isObject(value) && 'id' in value && typeof value.id === 'string') {
        const pointer = pointers.getPointer(value.id, namespace)
        if (dataModel.isPersistentDataNode(pointer)) {
            dependenciesBuilder.references.addNonOwnedRef(value.id, namespace)
            item.to = addHash(value.id)
        }
    }
}

export const resolveDataItem = (
    extensionContext: CreateExtArgs,
    itemPointer: Pointer,
    pagePointer: CompRef,
    dependenciesBuilder: DependenciesBuilder,
    componentType?: string
): DalValue => {
    const {extensionAPI, coreConfig} = extensionContext
    const {schemaService} = coreConfig
    const {dataModel, schemaAPI} = extensionAPI as DataModelExtensionAPI & SchemaExtensionAPI

    const serializedDataItem = dataModel.getItem(
        itemPointer.id,
        itemPointer.type,
        pagePointer.id,
        undefined,
        undefined,
        true
    )
    const dataItem = adaptDataItemForExport(
        schemaService,
        extensionAPI,
        serializedDataItem,
        itemPointer.type,
        schemaAPI,
        componentType
    )
    const isBuilderComponent = !!componentType && schemaAPI.isSchemaCollectionRegistered(componentType)

    forEachReferredDataItem(
        schemaService,
        dataItem,
        itemPointer.type,
        ({item, namespace, refFieldsInfo}: ReferredDataItemsIterateeContext) => {
            dependenciesBuilder.references.addInternalRef(item.id, namespace)
            const isPersistentDataNode =
                item.id && dataModel.isPersistentDataNode(extensionContext.pointers.getPointer(item.id, namespace))
            sanitizeDataItem(item, namespace, isBuilderComponent, isPersistentDataNode)
            if (item.type === 'RefArray') {
                replaceRefArrayPersistentRefs(extensionContext, item, namespace, dependenciesBuilder)
            }
            if (item.type === 'VariantRelation') {
                replaceVariantRelationPersistentRefs(extensionContext, item, namespace, dependenciesBuilder)
            }
            collectNonOwnedReferences(extensionContext, item, namespace, refFieldsInfo, dependenciesBuilder)
        }
    )

    return dataItem
}

export const getResolvedDataItemsByNamespaceMap = (
    extensionContext: CreateExtArgs,
    dataPointers: Pointer[],
    pagePointer: CompRef,
    dependenciesBuilder: DependenciesBuilder
): NamespacesDataMap | undefined => {
    if (dataPointers.length === 0) {
        return
    }

    const dataItemsMap: NamespacesDataMap = {}
    for (const pointer of dataPointers) {
        const dataItem = resolveDataItem(extensionContext, pointer, pagePointer, dependenciesBuilder)
        dataItemsMap[pointer.type] ??= {}
        dataItemsMap[pointer.type][pointer.id] = dataItem
    }

    return dataItemsMap
}

const removeReference = (
    {dal, pointers}: CreateExtArgs,
    referringPointer: Pointer,
    path: string[],
    {isList}: RefInfo
) => {
    if (isList) {
        const itemIndex = _.toNumber(path.pop())
        const refsListPointer = pointers.getInnerPointer(referringPointer, path)
        const refsList = [...dal.get(refsListPointer)]
        refsList.splice(itemIndex, 1)
        dal.set(refsListPointer, refsList)
    } else {
        const referencePointer = pointers.getInnerPointer(referringPointer, path)
        dal.remove(referencePointer)
    }
}

const removeReferencesToItem = (extensionContext: CreateExtArgs, itemPointer: Pointer, namespace: string) => {
    const {dal, extensionAPI} = extensionContext
    const {relationships, schemaAPI} = extensionAPI as RelationshipsAPI & SchemaExtensionAPI
    const referringPointers = relationships.getReferencesToPointer(itemPointer)
    for (const referringPointer of referringPointers) {
        const referringItem = dal.get(referringPointer)
        const refFieldsInfo = schemaAPI.extractReferenceFieldsInfo(referringPointer.type, referringItem.type)
        for (const refFieldInfo of refFieldsInfo) {
            if (refFieldInfo.referencedMap !== namespace) {
                continue
            }

            const referredPointers = getDetailedValueByPath(referringItem, refFieldInfo.path)
            for (const {value, path} of referredPointers) {
                if (stripHashIfExists(value) === itemPointer.id) {
                    removeReference(extensionContext, referringPointer, path, refFieldInfo)
                }
            }
        }
    }
}

const removeOriginalItem = (extensionContext: CreateExtArgs, originalItemId: string, namespace: string) => {
    const {dal, pointers} = extensionContext
    const originalItemPointer = pointers.getPointer(originalItemId, namespace)
    removeReferencesToItem(extensionContext, originalItemPointer, namespace)
    dal.remove(originalItemPointer)
}

const defaultItemIdAccessor = (item: DalValue): ItemIdAccessor => ({
    get: () => item.id,
    set: (id: string) => {
        item.id = id
    }
})

const resolveItemIdAccessor = (item: DalValue, namespace: string): ItemIdAccessor => {
    for (const adapter of dataItemAdapters) {
        const accessor = adapter.resolveItemId?.(item, namespace)
        if (accessor) {
            return accessor
        }
    }
    return defaultItemIdAccessor(item)
}

const replaceSingleItemReferences =
    (extensionContext: CreateExtArgs, dependenciesManager: DependenciesManager) =>
    ({item, namespace, refFieldsInfo, originalItem}: ReferredDataItemsIterateeContext) => {
        const {extensionAPI, pointers} = extensionContext
        const {dataModel} = extensionAPI as DataModelExtensionAPI
        const idAccessor = resolveItemIdAccessor(item, namespace)
        const effectiveId = idAccessor.get()
        const effectiveOriginalId = originalItem ? resolveItemIdAccessor(originalItem, namespace).get() : undefined
        if (
            !_.isPlainObject(item) ||
            (effectiveId && dataModel.isPersistentDataNode(pointers.getPointer(effectiveId, namespace)))
        ) {
            return
        }
        const newId = dependenciesManager.references.getNewItemId(effectiveId, namespace, effectiveOriginalId)
        idAccessor.set(newId)

        if (effectiveOriginalId && effectiveOriginalId !== newId) {
            removeOriginalItem(extensionContext, effectiveOriginalId, namespace)
        }

        for (const refFieldInfo of refFieldsInfo) {
            const {isRefOwner, path} = refFieldInfo

            if (isRefOwner) {
                continue
            }

            const valueByPath: DetailedValue[] = getDetailedValueByPath(item, path)
            const handleExternals: ExternalsHandler = getExternalsHandler(namespace, item.type, path)

            for (const detailedValue of valueByPath) {
                if (typeof detailedValue.value !== 'string') {
                    continue
                }
                const refId = stripHashIfExists(detailedValue.value)
                const effectiveRefFieldInfo: ExtendedRefInfo = {
                    ...refFieldInfo,
                    referencedMap: getEffectiveReferencedMap(
                        extensionContext,
                        refId,
                        namespace,
                        item.type,
                        refFieldInfo,
                        candidateMap => Boolean(dependenciesManager.references.getTarget(refId, candidateMap))
                    )
                }
                handleExternals.unpack(detailedValue, dependenciesManager, effectiveRefFieldInfo, item)
            }
        }
    }

const replaceRefArrayPersistentRefsToValues = (
    extensionContext: CreateExtArgs,
    {item, namespace}: ReferredDataItemsIterateeContext
) => {
    if (item.type !== 'RefArray') {
        return
    }

    const {extensionAPI, pointers} = extensionContext
    const {dataModel} = extensionAPI as DataModelExtensionAPI

    item.values = (item.values ?? []).map((value: string | Record<string, unknown>) => {
        if (_.isString(value)) {
            const refId = stripHashIfExists(value)
            const pointer = pointers.getPointer(refId, namespace)
            if (dataModel.isPersistentDataNode(pointer)) {
                const originalPersistentItem = dataModel.getItem(
                    refId,
                    namespace,
                    MASTER_PAGE_ID,
                    undefined,
                    undefined,
                    true
                )
                if (originalPersistentItem) {
                    return originalPersistentItem
                }
                throw new ReportableError({
                    errorType: 'PersistentDataNodeNotFound',
                    message: `Persistent data node ${refId} not found in namespace ${namespace}`
                })
            }
        }
        return value
    })
}

const replaceVariantRelationPersistentRefsToValues = (
    extensionContext: CreateExtArgs,
    {item, namespace}: ReferredDataItemsIterateeContext
) => {
    if (item.type !== 'VariantRelation') {
        return
    }

    const {extensionAPI, pointers} = extensionContext
    const {dataModel} = extensionAPI as DataModelExtensionAPI

    if (_.isString(item.to)) {
        const refId = stripHashIfExists(item.to)
        const pointer = pointers.getPointer(refId, namespace)
        if (dataModel.isPersistentDataNode(pointer)) {
            const originalPersistentItem = dataModel.getItem(
                refId,
                namespace,
                MASTER_PAGE_ID,
                undefined,
                undefined,
                true
            )
            if (originalPersistentItem) {
                item.to = originalPersistentItem
                return
            }
            throw new ReportableError({
                errorType: 'PersistentDataNodeNotFound',
                message: `Persistent data node ${refId} not found in namespace ${namespace}`
            })
        }
    }
}

const replaceDataItemReferences = (
    extensionContext: CreateExtArgs,
    dataItem: DalValue,
    origDataItem: DalValue,
    namespace: string,
    dependenciesManager: DependenciesManager
) => {
    const {coreConfig} = extensionContext
    const {schemaService} = coreConfig

    forEachReferredDataItem(
        schemaService,
        dataItem,
        namespace,
        (...args) => {
            replaceRefArrayPersistentRefsToValues(extensionContext, ...args)
            replaceVariantRelationPersistentRefsToValues(extensionContext, ...args)
            replaceSingleItemReferences(extensionContext, dependenciesManager)(...args)
        },
        {
            originalItem: origDataItem
        }
    )
}

export const updateComponentDataItem = (
    extensionContext: CreateExtArgs,
    componentPointer: CompRef,
    dataItem: DalValue,
    {query, namespace}: ComponentFeature,
    dependenciesManager: DependenciesManager,
    componentType?: string
) => {
    const {extensionAPI, dal, coreConfig, pointers} = extensionContext
    const {schemaService} = coreConfig
    const {dataModel, schemaAPI} = extensionAPI as DataModelExtensionAPI & SchemaExtensionAPI
    const compType = componentType ?? dal.get(componentPointer)?.componentType

    const flatOrigDataItem = dataModel.components.getItemByQuery(componentPointer, query, {raw: true})
    const origDataItem = adaptDataItemForExport(
        schemaService,
        extensionAPI,
        flatOrigDataItem,
        namespace,
        schemaAPI,
        compType
    )
    const isPersistent = origDataItem?.id
        ? dataModel.isPersistentDataNode(pointers.getPointer(origDataItem.id, namespace))
        : false

    if (dataItem) {
        replaceDataItemReferences(extensionContext, dataItem, origDataItem, namespace, dependenciesManager)
        const adaptedDataItem = adaptDataItemForImport(
            schemaService,
            extensionAPI,
            dataItem,
            namespace,
            schemaAPI,
            compType
        )
        dataModel.components.addItemByQuery(componentPointer, query, adaptedDataItem, {
            skipOriginalNodeMerge: true,
            raw: true
        })
    } else if (origDataItem && !isPersistent) {
        dataModel.components.removeItemByQuery(componentPointer, query)
    }
}

const updateDataItem = (
    extensionContext: CreateExtArgs,
    dataItem: DalValue,
    serializedOrigDataItem: DalValue,
    namespace: string,
    pagePointer: CompRef,
    dependenciesManager: DependenciesManager,
    componentType?: string
) => {
    const {pointers, extensionAPI, coreConfig} = extensionContext
    const {dataModel, schemaAPI} = extensionAPI as DataModelExtensionAPI & SchemaExtensionAPI
    const origDataItem = adaptDataItemForExport(
        coreConfig.schemaService,
        extensionAPI,
        serializedOrigDataItem,
        namespace,
        schemaAPI,
        componentType
    )

    if (dataItem) {
        replaceDataItemReferences(extensionContext, dataItem, origDataItem, namespace, dependenciesManager)
        const adaptedDataItem = adaptDataItemForImport(
            coreConfig.schemaService,
            extensionAPI,
            dataItem,
            namespace,
            schemaAPI,
            componentType
        )
        dataModel.addItemWithRefReuse(adaptedDataItem, namespace, pagePointer.id, undefined, undefined, {raw: true})
    } else if (origDataItem) {
        dataModel.removeItemRecursivelyIncludingPermanentNodes(pointers.getPointer(origDataItem.id, namespace))
    }
}

export const updateDataItemsByNamespacesDataMap = (
    extensionContext: CreateExtArgs,
    origPointers: Pointer[],
    namespacesDataMap: NamespacesDataMap | undefined,
    pagePointer: CompRef,
    dependenciesManager: DependenciesManager
) => {
    const {pointers, extensionAPI} = extensionContext
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const updates: DataItemsUpdates = origPointers.map((pointer: Pointer) => {
        const {id, type} = pointer
        const origDataItem = dataModel.getItem(id, type, pagePointer.id, undefined, undefined, true)
        const dataItem = namespacesDataMap?.[type]?.[id]
        delete namespacesDataMap?.[type]?.[id]

        return {
            pointer,
            origDataItem,
            dataItem
        }
    })

    _.forOwn(namespacesDataMap, (items: Record<string, DalValue>, namespace: string) => {
        _.forOwn(items, (dataItem: DalValue, id: string) => {
            updates.push({
                pointer: pointers.getPointer(id, namespace),
                dataItem
            })
        })
    })

    for (const {pointer, dataItem, origDataItem} of updates) {
        if (dataItem) {
            dataItem.id ??= pointer.id
            dependenciesManager.references.addReference(dataItem.id, pointer.type)
        }

        updateDataItem(extensionContext, dataItem, origDataItem, pointer.type, pagePointer, dependenciesManager)
    }
}
